import { fixedPartAt } from './fees';
import { friendlyBelow } from './round';
import type { ChannelInput, RoundingRule } from './types';

/**
 * Price breakpoints where a channel's fixed fee or shipping share can change, ascending, from 0.
 * Fee ranges are inclusive ("De R$ 0,00 até R$ 78,99"), so a range ending at `to` starts a new
 * segment at `to + 1`.
 */
export function breakpoints(channel: ChannelInput): number[] {
  const points = new Set<number>([0]);
  for (const r of channel.fixedFees) {
    points.add(Math.max(0, r.fromCentavos));
    if (r.toCentavos !== null) points.add(r.toCentavos + 1);
  }
  if (channel.shipping?.fromCentavos != null) points.add(channel.shipping.fromCentavos);
  return [...points].toSorted((a, b) => a - b);
}

/** Share of the price that is not available to cover cost (percentages + margin). */
export function percentOfPriceBps(
  channel: ChannelInput,
  taxBps: number,
  marginBps: number,
): number {
  return channel.commissionBps + channel.paymentFeeBps + taxBps + marginBps;
}

/**
 * Solves price = (unit cost + fixed fee(price) + shipping(price)) / (1 − commission − payment − tax − margin).
 * Fixed fees depend on the price range, so each range segment is solved with its own fees and the
 * lowest price that lands inside its own segment wins (the spec's "recompute until the range stops
 * changing", without the risk of oscillating). Returns null when the denominator is 0 or negative.
 */
export function solvePrice(
  unitCost: number,
  channel: ChannelInput,
  taxBps: number,
  marginBps: number,
): number | null {
  const denominator = 1 - percentOfPriceBps(channel, taxBps, marginBps) / 10_000;
  if (denominator <= 0) return null;

  const points = breakpoints(channel);
  for (let i = 0; i < points.length; i++) {
    const start = points[i] ?? 0;
    const next = points[i + 1] ?? Number.POSITIVE_INFINITY;
    const price = (unitCost + fixedPartAt(start, channel)) / denominator;
    if (price >= start && price < next) return price;
  }

  // No self-consistent segment (a fee drop just above a threshold): the lowest segment start whose
  // own fees already reach the margin.
  for (const start of points) {
    if (start > 0 && start * denominator >= unitCost + fixedPartAt(start, channel)) return start;
  }
  return null;
}

/**
 * The nearest threshold at or below `price` where fees change, and the best friendly price under it.
 * Used for the free-shipping / fixed-fee "cliff" warning.
 */
export function cliffBelow(
  price: number,
  channel: ChannelInput,
  rounding: RoundingRule,
): { threshold: number; candidate: number; freeShipping: boolean } | null {
  const points = breakpoints(channel).filter((p) => p > 0 && p <= price);
  for (const threshold of points.toReversed()) {
    if (fixedPartAt(threshold, channel) === fixedPartAt(threshold - 1, channel)) continue;
    const candidate = friendlyBelow(threshold, rounding);
    if (candidate <= 0) return null;
    const shippingAt = (p: number) =>
      channel.shipping &&
      (channel.shipping.fromCentavos === null || p >= channel.shipping.fromCentavos);
    return {
      threshold,
      candidate,
      freeShipping: Boolean(shippingAt(threshold)) && !shippingAt(threshold - 1),
    };
  }
  return null;
}
